import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Sidebar from "../Dashboard/Sidebar";
import Loader from "../layouts/Loader";
import MetaData from "../layouts/MetaData";
import {
    getAccountCategories,
    getDaySummary,
    getMonthlySummary,
    getCategorySummary,
    addAccountEntry,
    deleteAccountEntry,
    setOpeningBalance
} from "../../actions/accountActions";
import {
    clearEntryAdded,
    clearEntryDeleted,
    clearOpeningSet,
    clearAccountError
} from "../../slices/accountSlice";
import formatDate, { toInputDate } from "../../utils/formatDate";
import downloadCsv from "../../utils/downloadCsv";
import DeleteConfirmModal from "../layouts/DeleteConfirmModal";
import AppDatePicker from "../common/AppDatePicker";

const currency = (n) =>
    `\u20b9${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MONTH_NAMES = Array.from({ length: 12 }, (_, i) => new Date(2000, i, 1).toLocaleString("default", { month: "long" }));
const ALL_CATEGORIES = "__all__";

export default function Accounts() {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.authState);
    const { loading, categories, day, monthly, byCategory, categoryEntries, isEntryAdded, isEntryDeleted, isOpeningSet, error } =
        useSelector((state) => state.accountState);

    const [tab, setTab] = useState("day");

    const [date, setDate] = useState(toInputDate());
    const [showOpeningModal, setShowOpeningModal] = useState(false);
    const [editOpeningAmount, setEditOpeningAmount] = useState("");
    const [entryType, setEntryType] = useState("income");
    const [category, setCategory] = useState("");
    const [amount, setAmount] = useState("");
    const [description, setDescription] = useState("");

    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);

    const [catType, setCatType] = useState("expense");
    const [catCategory, setCatCategory] = useState(ALL_CATEGORIES);
    const [catMonth, setCatMonth] = useState(now.getMonth() + 1);
    const [catYear, setCatYear] = useState(now.getFullYear());
    const [catFrom, setCatFrom] = useState("");
    const [catTo, setCatTo] = useState("");

    useEffect(() => {
        dispatch(getAccountCategories());
    }, [dispatch]);

    useEffect(() => {
        if (tab === "day") dispatch(getDaySummary(date));
    }, [dispatch, tab, date]);

    useEffect(() => {
        if (tab === "monthly") dispatch(getMonthlySummary(year, month));
    }, [dispatch, tab, year, month]);

    // whenever the quick month/year changes, drive the from/to range for category-wise
    useEffect(() => {
        const from = new Date(catYear, catMonth - 1, 1);
        const to = new Date(catYear, catMonth, 0);
        setCatFrom(toInputDate(from));
        setCatTo(toInputDate(to));
    }, [catMonth, catYear]);

    useEffect(() => {
        if (tab === "category" && catFrom && catTo) {
            const categoryFilter = catCategory === ALL_CATEGORIES ? undefined : catCategory;
            dispatch(getCategorySummary(catType, catFrom, catTo, categoryFilter));
        }
    }, [dispatch, tab, catType, catCategory, catFrom, catTo]);

    // switching income/expense resets the category drill-down back to "All"
    useEffect(() => {
        setCatCategory(ALL_CATEGORIES);
    }, [catType]);

    useEffect(() => {
        const categoryList = entryType === "income" ? categories.income : categories.expense;
        if (categoryList?.length && !categoryList.includes(category)) {
            setCategory(categoryList[0]);
        }
    }, [entryType, categories, category]);

    useEffect(() => {
        dispatch(clearEntryAdded());
        dispatch(clearEntryDeleted());
        dispatch(clearOpeningSet());
        dispatch(clearAccountError());
        return () => {
            dispatch(clearEntryAdded());
            dispatch(clearEntryDeleted());
            dispatch(clearOpeningSet());
            dispatch(clearAccountError());
        };
    }, [dispatch]);

    useEffect(() => {
        if (error) {
            toast(error, { type: "error" });
            dispatch(clearAccountError());
        }
        if (isEntryAdded) {
            toast("Entry added", { type: "success" });
            dispatch(clearEntryAdded());
            setAmount("");
            setDescription("");
            dispatch(getDaySummary(date));
        }
        if (isEntryDeleted) {
            toast("Entry deleted", { type: "success" });
            dispatch(clearEntryDeleted());
            dispatch(getDaySummary(date));
        }
        if (isOpeningSet) {
            toast("Opening balance updated successfully", { type: "success" });
            dispatch(clearOpeningSet());
            setShowOpeningModal(false);
            dispatch(getDaySummary(date));
        }
    }, [error, isEntryAdded, isEntryDeleted, isOpeningSet, dispatch, date]);

    const [deleteModal, setDeleteModal] = useState({ show: false, id: null, name: '' });

    const handleDelete = (id, description, amount, category) => {
        const name = description || `${category} - ${currency(amount)}`;
        setDeleteModal({ show: true, id, name });
    };

    const confirmDelete = () => {
        dispatch(deleteAccountEntry(deleteModal.id));
        setDeleteModal({ show: false, id: null, name: '' });
    };

    const submitEntry = (e) => {
        e.preventDefault();
        if (!amount || Number(amount) <= 0) {
            toast("Enter a valid amount", { type: "error" });
            return;
        }
        dispatch(addAccountEntry({ date, type: entryType, category, amount: Number(amount), description }));
    };


    const downloadMonthlyReport = () => {
        if (!monthly?.entries || monthly.entries.length === 0) {
            toast("Nothing to download for this month", { type: "warning" });
            return;
        }
        downloadCsv(
            `accounts-${MONTH_NAMES[month - 1]}-${year}.csv`,
            [
                { key: "date", label: "Date" },
                { key: "type", label: "Type" },
                { key: "category", label: "Category" },
                { key: "patient", label: "Patient" },
                { key: "description", label: "Description" },
                { key: "amount", label: "Amount" }
            ],
            monthly.entries.map((en) => ({
                date: formatDate(en.date),
                type: en.type,
                category: en.category,
                patient: en.patientName || en.Bill?.patient?.name || "-",
                description: en.description || (en.autoGenerated ? "Auto - bill payment" : ""),
                amount: en.amount
            }))
        );
    };

    const downloadCategoryReport = () => {
        const isDrilledDown = catCategory !== ALL_CATEGORIES;

        if (isDrilledDown) {
            if (!categoryEntries || categoryEntries.length === 0) {
                toast("Nothing to download for this selection", { type: "warning" });
                return;
            }
            downloadCsv(
                `accounts-${catCategory}-${MONTH_NAMES[catMonth - 1]}-${catYear}.csv`,
                [
                    { key: "date", label: "Date" },
                    { key: "patient", label: "Patient" },
                    { key: "description", label: "Description" },
                    { key: "amount", label: "Amount" }
                ],
                categoryEntries.map((en) => ({
                    date: formatDate(en.date),
                    patient: en.patientName || en.Bill?.patient?.name || "-",
                    description: en.description || (en.autoGenerated ? "Auto - bill payment" : ""),
                    amount: en.amount
                }))
            );
            return;
        }

        if (!byCategory || byCategory.length === 0) {
            toast("Nothing to download for this range", { type: "warning" });
            return;
        }
        downloadCsv(
            `accounts-${catType}-${MONTH_NAMES[catMonth - 1]}-${catYear}.csv`,
            [
                { key: "category", label: "Category" },
                { key: "type", label: "Type" },
                { key: "count", label: "Entries" },
                { key: "total", label: "Total" }
            ],
            byCategory
        );
    };

    const categoryOptions = entryType === "income" ? categories.income : categories.expense;
    const catCategoryOptions = catType === "income" ? categories.income : categories.expense;
    const categoryTotal = (byCategory || []).reduce((s, c) => s + c.total, 0);
    const drilledDownTotal = (categoryEntries || []).reduce((s, e) => s + e.amount, 0);
    const isDrilledDown = catCategory !== ALL_CATEGORIES;

    return (
        <div className="row g-0">
      <div className="col-12 col-md-2"><Sidebar /></div>
      <div className="col-12 col-md-10 main-content">
                <MetaData title={"Accounts"} />
                <h3 className="my-4">Accounts</h3>

                <ul className="nav nav-tabs mb-3">
                    <li className="nav-item">
                        <button className={`nav-link ${tab === "day" ? "active" : ""}`} onClick={() => setTab("day")}>Daily</button>
                    </li>
                    <li className="nav-item">
                        <button className={`nav-link ${tab === "monthly" ? "active" : ""}`} onClick={() => setTab("monthly")}>Monthly</button>
                    </li>
                    <li className="nav-item">
                        <button className={`nav-link ${tab === "category" ? "active" : ""}`} onClick={() => setTab("category")}>Category-wise</button>
                    </li>
                </ul>

                {loading ? <Loader /> : (
                    <Fragment>
                        {tab === "day" && (
                            <Fragment>
                                <div className="mb-3" style={{ maxWidth: 220 }}>
                                    <AppDatePicker
                                        label="Date"
                                        value={date}
                                        onChange={(val) => setDate(val)}
                                    />
                                </div>

                                <div className="row g-2 mb-3 accounts-stat-row">
                                    <div className="col-6 col-md-3">
                                        <div className="stat-tile">
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <div className="text-muted small">Opening Balance</div>
                                                {date === '2026-09-01' && user?.role === 'master' && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-primary py-0 px-2"
                                                        style={{ fontSize: '0.75rem' }}
                                                        onClick={() => {
                                                            setEditOpeningAmount(day?.openingBalance !== undefined ? day.openingBalance : 0);
                                                            setShowOpeningModal(true);
                                                        }}
                                                    >
                                                        <i className="fa fa-pencil me-1"></i> Edit
                                                    </button>
                                                )}
                                            </div>
                                            <div className="fw-bold fs-5 stat-amount text-truncate" title={currency(day?.openingBalance)}>{currency(day?.openingBalance)}</div>
                                            {date === '2026-09-01' ? (
                                                <span className="badge bg-info text-dark" style={{ fontSize: '0.65rem' }}>
                                                    Org Start Date {user?.role === 'master' ? '(Master Editable)' : ''}
                                                </span>
                                            ) : (
                                                <span className="text-muted d-block" style={{ fontSize: '0.7rem' }}>
                                                    Follows previous closing
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                        <div className="stat-tile">
                                            <div className="text-muted small">Income</div>
                                            <div className="fw-bold fs-5 text-success stat-amount text-truncate" title={currency(day?.income)}>{currency(day?.income)}</div>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                        <div className="stat-tile">
                                            <div className="text-muted small">Expense</div>
                                            <div className="fw-bold fs-5 text-danger stat-amount text-truncate" title={currency(day?.expense)}>{currency(day?.expense)}</div>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                        <div className="stat-tile">
                                            <div className="text-muted small">Closing Balance</div>
                                            <div className="fw-bold fs-5 stat-amount text-truncate" title={currency(day?.closingBalance)}>{currency(day?.closingBalance)}</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="row g-3">
                                    <div className="col-md-7">
                                        <h6>Entries</h6>
                                        {day?.entries?.length > 0 ? (
                                            <div className="table-responsive">
                                            <table className="table table-sm table-bordered">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Type</th>
                                                        <th>Category</th>
                                                        <th>Amount</th>
                                                        <th>Description</th>
                                                        <th></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {day.entries.map((en) => (
                                                        <tr key={en._id}>
                                                            <td>
                                                                <span className={`badge ${en.type === "income" ? "bg-success" : "bg-danger"}`}>
                                                                    {en.type}
                                                                </span>
                                                            </td>
                                                            <td>{en.category}</td>
                                                            <td>{currency(en.amount)}</td>
                                                            <td>
                                                                <div className="fw-medium text-dark">{en.description || (en.autoGenerated ? "Auto - bill payment" : "-")}</div>
                                                                {(en.patientName || en.Bill?.patient?.name) && (
                                                                    <div className="mt-1">
                                                                        <span
                                                                            className="badge"
                                                                            style={{
                                                                                background: 'rgba(13,148,136,0.1)',
                                                                                color: 'var(--primary)',
                                                                                border: '1px solid rgba(13,148,136,0.25)',
                                                                                fontSize: '0.75rem',
                                                                                fontWeight: 600
                                                                            }}
                                                                        >
                                                                            <i className="fa fa-user me-1"></i>
                                                                            {en.patientName || en.Bill?.patient?.name}
                                                                            {(en.patientCustomId || en.Bill?.patient?.patientId) ? ` (${en.patientCustomId || en.Bill?.patient?.patientId})` : ''}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <button
                                                                    className="btn btn-sm btn-outline-danger"
                                                                    onClick={() => handleDelete(en._id, en.description, en.amount, en.category)}
                                                                >
                                                                    &times;
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            </div>
                                        ) : (
                                            <p className="text-muted">No entries for this day.</p>
                                        )}
                                    </div>

                                    <div className="col-md-5">
                                        <div className="border rounded p-3 mb-3">
                                            <h6>Add Entry</h6>
                                            <form onSubmit={submitEntry}>
                                                <div className="form-group mb-2">
                                                    <label className="small mb-0">Type</label>
                                                    <select className="form-select" value={entryType} onChange={(e) => setEntryType(e.target.value)}>
                                                        <option value="income">Income</option>
                                                        <option value="expense">Expense</option>
                                                    </select>
                                                </div>
                                                <div className="form-group mb-2">
                                                    <label className="small mb-0">Category</label>
                                                    <select className="form-select" value={category} onChange={(e) => setCategory(e.target.value)}>
                                                        {categoryOptions?.map((c) => (
                                                            <option key={c} value={c}>{c}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="form-group mb-2">
                                                    <label className="small mb-0">Amount</label>
                                                    <input
                                                        type="number" min="0.01" step="0.01"
                                                        className="form-control"
                                                        value={amount}
                                                        onChange={(e) => setAmount(e.target.value)}
                                                    />
                                                </div>
                                                <div className="form-group mb-2">
                                                    <label className="small mb-0">Description (optional)</label>
                                                    <input className="form-control" value={description} onChange={(e) => setDescription(e.target.value)} />
                                                </div>
                                                <button className="btn btn-primary w-100" type="submit" disabled={loading}>Add</button>
                                            </form>
                                        </div>


                                    </div>
                                </div>
                            </Fragment>
                        )}

                        {tab === "monthly" && (
                            <Fragment>
                                <div className="d-flex justify-content-between flex-wrap gap-2 mb-3">
                                    <div className="d-flex gap-2">
                                        <select className="form-select" style={{ maxWidth: 150 }} value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                                            {MONTH_NAMES.map((name, i) => (
                                                <option key={name} value={i + 1}>{name}</option>
                                            ))}
                                        </select>
                                        <input
                                            type="number" className="form-control" style={{ maxWidth: 120 }}
                                            value={year} onChange={(e) => setYear(Number(e.target.value))}
                                        />
                                    </div>
                                    <button className="btn btn-outline-secondary" onClick={downloadMonthlyReport}>
                                        &#8681; Download CSV
                                    </button>
                                </div>

                                <div className="row g-2 mb-3 accounts-stat-row">
                                    <div className="col-6 col-md-3">
                                        <div className="stat-tile">
                                            <div className="text-muted small">Opening Balance</div>
                                            <div className="fw-bold fs-5 stat-amount text-truncate" title={currency(monthly?.openingBalance)}>{currency(monthly?.openingBalance)}</div>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                        <div className="stat-tile">
                                            <div className="text-muted small">Total Income</div>
                                            <div className="fw-bold fs-5 text-success stat-amount text-truncate" title={currency(monthly?.totalIncome)}>{currency(monthly?.totalIncome)}</div>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                        <div className="stat-tile">
                                            <div className="text-muted small">Total Expense</div>
                                            <div className="fw-bold fs-5 text-danger stat-amount text-truncate" title={currency(monthly?.totalExpense)}>{currency(monthly?.totalExpense)}</div>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                        <div className="stat-tile">
                                            <div className="text-muted small">Closing Balance</div>
                                            <div className="fw-bold fs-5 stat-amount text-truncate" title={currency(monthly?.closingBalance)}>{currency(monthly?.closingBalance)}</div>
                                        </div>
                                    </div>
                                </div>

                                <h6>Entries</h6>
                                {monthly?.entries?.length > 0 ? (
                                    <div className="table-responsive">
                                        <table className="table table-sm table-bordered">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Date</th>
                                                    <th>Type</th>
                                                    <th>Category</th>
                                                    <th>Description</th>
                                                    <th>Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {monthly.entries.map((en) => (
                                                    <tr key={en._id}>
                                                        <td>{formatDate(en.date)}</td>
                                                        <td>
                                                            <span className={`badge ${en.type === "income" ? "bg-success" : "bg-danger"}`}>
                                                                {en.type}
                                                            </span>
                                                        </td>
                                                        <td>{en.category}</td>
                                                        <td>
                                                                <div className="fw-medium text-dark">{en.description || (en.autoGenerated ? "Auto - bill payment" : "-")}</div>
                                                                {(en.patientName || en.Bill?.patient?.name) && (
                                                                    <div className="mt-1">
                                                                        <span
                                                                            className="badge"
                                                                            style={{
                                                                                background: 'rgba(13,148,136,0.1)',
                                                                                color: 'var(--primary)',
                                                                                border: '1px solid rgba(13,148,136,0.25)',
                                                                                fontSize: '0.75rem',
                                                                                fontWeight: 600
                                                                            }}
                                                                        >
                                                                            <i className="fa fa-user me-1"></i>
                                                                            {en.patientName || en.Bill?.patient?.name}
                                                                            {(en.patientCustomId || en.Bill?.patient?.patientId) ? ` (${en.patientCustomId || en.Bill?.patient?.patientId})` : ''}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </td>
                                                        <td className={en.type === "income" ? "text-success" : "text-danger"}>{currency(en.amount)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <p className="text-muted">No entries for this month.</p>
                                )}
                            </Fragment>
                        )}

                        {tab === "category" && (
                            <Fragment>
                                {/* Filter controls — main row */}
                                <div className="row g-2 mb-2 align-items-end">
                                    <div className="col-6 col-sm-3 col-md-2">
                                        <label className="form-label">Type</label>
                                        <select className="form-select" value={catType} onChange={(e) => setCatType(e.target.value)}>
                                            <option value="expense">Expense</option>
                                            <option value="income">Income</option>
                                        </select>
                                    </div>
                                    <div className="col-6 col-sm-3 col-md-2">
                                        <label className="form-label">Category</label>
                                        <select className="form-select" value={catCategory} onChange={(e) => setCatCategory(e.target.value)}>
                                            <option value={ALL_CATEGORIES}>All categories</option>
                                            {catCategoryOptions?.map((c) => (
                                                <option key={c} value={c}>{c}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-6 col-sm-3 col-md-2">
                                        <label className="form-label">Month</label>
                                        <select className="form-select" value={catMonth} onChange={(e) => setCatMonth(Number(e.target.value))}>
                                            {MONTH_NAMES.map((name, i) => (
                                                <option key={name} value={i + 1}>{name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-6 col-sm-3 col-md-2">
                                        <label className="form-label">Year</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={catYear}
                                            onChange={(e) => setCatYear(Number(e.target.value))}
                                        />
                                    </div>
                                </div>

                                {/* Custom date range + download — second row */}
                                <div className="row g-2 mb-3 align-items-end">
                                    <div className="col-12 col-sm-8 col-md-6">
                                        <label className="form-label">Custom date range</label>
                                        <div className="d-flex gap-2 flex-column flex-sm-row">
                                            <AppDatePicker
                                                label="From Date"
                                                value={catFrom}
                                                onChange={(val) => setCatFrom(val)}
                                            />
                                            <AppDatePicker
                                                label="To Date"
                                                value={catTo}
                                                onChange={(val) => setCatTo(val)}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 col-sm-6 col-md-4 d-flex align-items-end">
                                        <button
                                            className="btn btn-outline-secondary"
                                            style={{ whiteSpace: 'nowrap' }}
                                            onClick={downloadCategoryReport}
                                        >
                                            <i className="fa fa-download me-1"></i> Download CSV
                                        </button>
                                    </div>
                                </div>

                                {isDrilledDown ? (
                                    <Fragment>
                                        <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                                            <h6 className="mb-0">
                                                {catCategory} &middot; {MONTH_NAMES[catMonth - 1]} {catYear}
                                            </h6>
                                            <span className={`fw-bold ${catType === "income" ? "text-success" : "text-danger"}`}>
                                                Total: {currency(drilledDownTotal)}
                                            </span>
                                        </div>

                                        {categoryEntries?.length > 0 ? (
                                            <div className="table-responsive">
                                            <table className="table table-sm table-bordered">
                                                <thead className="table-light">
                                                    <tr><th>Date</th><th>Description</th><th>Amount</th></tr>
                                                </thead>
                                                <tbody>
                                                    {categoryEntries.map((en) => (
                                                        <tr key={en._id}>
                                                            <td>{formatDate(en.date)}</td>
                                                            <td>
                                                                <div className="fw-medium text-dark">{en.description || (en.autoGenerated ? "Auto - bill payment" : "-")}</div>
                                                                {(en.patientName || en.Bill?.patient?.name) && (
                                                                    <div className="mt-1">
                                                                        <span
                                                                            className="badge"
                                                                            style={{
                                                                                background: 'rgba(13,148,136,0.1)',
                                                                                color: 'var(--primary)',
                                                                                border: '1px solid rgba(13,148,136,0.25)',
                                                                                fontSize: '0.75rem',
                                                                                fontWeight: 600
                                                                            }}
                                                                        >
                                                                            <i className="fa fa-user me-1"></i>
                                                                            {en.patientName || en.Bill?.patient?.name}
                                                                            {(en.patientCustomId || en.Bill?.patient?.patientId) ? ` (${en.patientCustomId || en.Bill?.patient?.patientId})` : ''}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td className={en.type === "income" ? "text-success" : "text-danger"}>{currency(en.amount)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            </div>
                                        ) : (
                                            <p className="text-muted">No "{catCategory}" entries for {MONTH_NAMES[catMonth - 1]} {catYear}.</p>
                                        )}
                                    </Fragment>
                                ) : (
                                    byCategory?.length > 0 ? (
                                        <div className="table-responsive">
                                        <table className="table table-sm table-bordered">
                                            <thead className="table-light">
                                                <tr><th>Category</th><th>Entries</th><th>Total</th><th></th></tr>
                                            </thead>
                                            <tbody>
                                                {byCategory.map((c) => (
                                                    <tr key={c.category} className="cursor-pointer" onClick={() => setCatCategory(c.category)} style={{ cursor: "pointer" }}>
                                                        <td>{c.category}</td>
                                                        <td>{c.count}</td>
                                                        <td className={c.type === "income" ? "text-success" : "text-danger"}>{currency(c.total)}</td>
                                                        <td className="text-end text-muted small">View &rarr;</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr className="fw-bold">
                                                    <td>Total</td>
                                                    <td></td>
                                                    <td>{currency(categoryTotal)}</td>
                                                    <td></td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                        </div>
                                    ) : (
                                        <p className="text-muted">No entries for this range.</p>
                                    )
                                )}
                            </Fragment>
                        )}
                        {showOpeningModal && (
                            <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                                <div className="modal-dialog modal-dialog-centered">
                                    <div className="modal-content">
                                        <div className="modal-header">
                                            <h5 className="modal-title">Edit Opening Balance (01/09/2026)</h5>
                                            <button type="button" className="btn-close" onClick={() => setShowOpeningModal(false)}></button>
                                        </div>
                                        <form onSubmit={(e) => {
                                            e.preventDefault();
                                            dispatch(setOpeningBalance('2026-09-01', Number(editOpeningAmount)));
                                        }}>
                                            <div className="modal-body">
                                                <div className="alert alert-info py-2 small mb-3">
                                                    <i className="fa fa-info-circle me-1"></i>
                                                    This sets the organization's initial opening balance on <strong>01/09/2026</strong>. All subsequent dates automatically follow previous closing balances. Only Master can edit this balance.
                                                </div>
                                                <div className="mb-3">
                                                    <label className="form-label fw-bold">Opening Balance Amount (&#8377;)</label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        className="form-control"
                                                        required
                                                        value={editOpeningAmount}
                                                        onChange={(e) => setEditOpeningAmount(e.target.value)}
                                                        placeholder="Enter initial opening balance"
                                                    />
                                                </div>
                                            </div>
                                            <div className="modal-footer">
                                                <button type="button" className="btn btn-secondary" onClick={() => setShowOpeningModal(false)}>Cancel</button>
                                                <button type="submit" className="btn btn-primary">Save Balance</button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        )}
                        <DeleteConfirmModal
                            show={deleteModal.show}
                            itemName={deleteModal.name}
                            onClose={() => setDeleteModal({ show: false, id: null, name: '' })}
                            onConfirm={confirmDelete}
                        />
                    </Fragment>
                )}
        </div>
        </div>
    );
}



